import { useState } from 'react';
import { i18n, prefixes, type UILanguageCode, useTypedTranslation } from '@/locales';
import { Input } from '@/components/_untitled/base/input/input';
import { Checkbox } from '@/components/_untitled/base/checkbox/checkbox';
import { AlertCircle } from '@untitledui/icons';
import { authApi } from '@/api/authApi';
import { useAuth } from '../../hooks/useAuth';
import type { KycStepProps } from '../../config/kycSteps.config';
import { StepFooter } from '../StepFooter';
import { PasswordFields } from '../PasswordFields';
import { isPasswordValid } from '../../utils/passwordValidation';
import { TERMS_VERSION } from '@/constants/versions';

export function AccountStep({ data, onComplete, onBack }: KycStepProps) {
  const [tAccount] = useTypedTranslation(prefixes('kyc.steps.account'));
  const { refetchUser } = useAuth();
  const [email, setEmail] = useState(data.email || '');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [newsletterConsent, setNewsletterConsent] = useState(false);
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canProceed = !!email && isPasswordValid(password) && password === confirmPassword;

  const handleRegister = async () => {
    try {
      setLoading(true);
      setError(null);

      await authApi.register(
          email,
          password,
          TERMS_VERSION,
          i18n.language as UILanguageCode,
          { newsletterConsent, marketingConsent }
      );

      // Fetch full user profile after registration
      await refetchUser();

      // Preferences already saved during registration, just move to next step
      onComplete({});
    } catch (err: unknown) {
      if (err && typeof err === 'object' && 'response' in err) {
        const axiosError = err as { response?: { data?: { message?: string | string[] } } };
        const backendMessage = axiosError.response?.data?.message;

        if (Array.isArray(backendMessage)) {
          setError(backendMessage.join(', '));
        } else if (typeof backendMessage === 'string') {
          setError(backendMessage);
        } else {
          setError('Registration failed');
        }
      } else {
        setError(err instanceof Error ? err.message : 'Registration failed');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Input
        type="email"
        label={ tAccount('email') }
        value={email}
        onChange={setEmail}
        aria-label={ tAccount('email') }
        isRequired
        isDisabled={!!data.email}
        data-testid="account-email"
      />

      <PasswordFields
        password={password}
        confirmPassword={confirmPassword}
        onPasswordChange={setPassword}
        onConfirmPasswordChange={setConfirmPassword}
      />

      <div className="flex flex-col gap-5 py-5">
        <Checkbox
            label={ tAccount('newsletterConsent') }
          isSelected={newsletterConsent}
          onChange={setNewsletterConsent}
          size="sm"
        />

        <Checkbox
            label={ tAccount('marketingConsent') }
          isSelected={marketingConsent}
          onChange={setMarketingConsent}
          size="sm"
        />
      </div>

      {error && (
        <div className="flex items-start gap-2 rounded-lg bg-error-50 px-3 py-2.5">
          <AlertCircle className="h-5 w-5 shrink-0 text-error-600 mt-0.5" />
          <div className="flex flex-col gap-0.5">
            <p className="text-sm font-semibold text-error-700">
              { tAccount('registrationError') }
            </p>
            <p className="text-sm text-error-600">{error}</p>
          </div>
        </div>
      )}

      <StepFooter
        primary={{
          label: tAccount('button'),
          onClick: handleRegister,
          disabled: !canProceed,
          loading
        }}
        back={{ show: true, onClick: onBack }}
      />
    </>
  );
}
